import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { errorMessage, listChildren, listTransitions, transitionEpic, updateEpic } from "./api";
import { StepBar, formatRelative } from "./components";

const STATUS_CLASS = { new: "todo", indeterminate: "progress", done: "done" };

function StatusPill({ status }) {
  if (!status) return null;

  return <span className={`pv-status pv-status--${STATUS_CLASS[status.category] || "todo"}`}>{status.name}</span>;
}

/** The epic's Jira status; click to pick one of the transitions Jira allows. */
function StatusMenu({ epic, onChanged }) {
  const [open, setOpen] = useState(false);
  const [transitions, setTransitions] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setTransitions(null);
    try {
      const data = await listTransitions(epic.key);
      setTransitions(data.transitions || []);
    } catch (err) {
      setOpen(false);
      toast.error(`${epic.key}: ${errorMessage(err)}`);
    }
  };

  const apply = async (transition) => {
    setBusy(true);
    try {
      const data = await transitionEpic(epic.key, transition.id);
      onChanged(data.status);
      toast.success(`${epic.key} is now ${data.status?.name || transition.to?.name}`);
      setOpen(false);
    } catch (err) {
      toast.error(`${epic.key}: ${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="pv-status-menu" ref={ref}>
      <button
        type="button"
        className="pv-status-btn"
        onClick={toggle}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Change the Jira status"
      >
        <StatusPill status={epic.status || { name: "No status", category: "new" }} />
        <span className="pv-caret">▾</span>
      </button>
      {open ? (
        <div className="pv-menu" role="menu">
          {transitions === null ? <div className="pv-menu-note">Loading from Jira…</div> : null}
          {transitions?.length === 0 ? <div className="pv-menu-note">No status changes allowed</div> : null}
          {(transitions || []).map((transition) => (
            <button
              key={transition.id}
              type="button"
              role="menuitem"
              className="pv-menu-item"
              disabled={busy}
              onClick={() => apply(transition)}
            >
              <span>{transition.name}</span>
              {transition.to && transition.to.name !== transition.name ? (
                <span className="pv-muted"> → {transition.to.name}</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
}

/** Title and description, editable in place. Formatted descriptions stay in Jira. */
function ProjectCell({ epic, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [summary, setSummary] = useState(epic.summary);
  const [description, setDescription] = useState(epic.description);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const start = () => {
    setSummary(epic.summary);
    setDescription(epic.description);
    setError("");
    setEditing(true);
  };

  const save = async (event) => {
    event.preventDefault();
    const changes = {};
    if (summary.trim() !== epic.summary) changes.summary = summary;
    if (epic.descriptionEditable && description.trim() !== epic.description) changes.description = description;
    if (!Object.keys(changes).length) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError("");
    try {
      await updateEpic(epic.key, changes);
      onSaved({
        ...(changes.summary !== undefined ? { summary: changes.summary.trim() } : {}),
        ...(changes.description !== undefined ? { description: changes.description.trim() } : {}),
      });
      toast.success(`${epic.key} saved to Jira`);
      setEditing(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <>
        <div className="pv-project-head">
          <a href={epic.url} target="_blank" rel="noreferrer" className="pv-project-title">
            {epic.summary}
          </a>
          <button type="button" className="pv-link-btn pv-edit-btn" onClick={start} aria-label={`Edit ${epic.key}`}>
            Edit
          </button>
        </div>
        {epic.description ? <p className="pv-project-desc">{epic.description}</p> : null}
      </>
    );
  }

  return (
    <form className="pv-inline-form" onSubmit={save}>
      <input
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        aria-label="Title"
        autoFocus
      />
      {epic.descriptionEditable ? (
        <textarea
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          aria-label="Description"
          placeholder="What this project is"
        />
      ) : (
        <p className="pv-note">
          This description has formatting, so it is edited in{" "}
          <a href={epic.url} target="_blank" rel="noreferrer">
            Jira
          </a>{" "}
          to keep it.
        </p>
      )}
      {error ? <div className="pv-error">{error}</div> : null}
      <div className="pv-inline-actions">
        <button type="submit" className="pv-btn pv-btn--primary" disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" className="pv-btn" onClick={() => setEditing(false)} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** The epic's tickets, loaded when the row is opened. */
function ChildrenRow({ epic }) {
  const [children, setChildren] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    listChildren(epic.key)
      .then((data) => !cancelled && setChildren(data.children || []))
      .catch((err) => !cancelled && setError(errorMessage(err)));

    return () => {
      cancelled = true;
    };
  }, [epic.key]);

  return (
    <tr className="pv-children-row">
      <td colSpan={3}>
        {error ? <div className="pv-error">{error}</div> : null}
        {!children && !error ? <div className="pv-muted">Loading tickets from Jira…</div> : null}
        {children?.length === 0 ? <div className="pv-muted">No tickets under this epic.</div> : null}
        {children?.length ? (
          <ul className="pv-children">
            {children.map((child) => (
              <li key={child.key}>
                <a href={child.url} target="_blank" rel="noreferrer" className="pv-key">
                  {child.key}
                </a>
                <span className="pv-child-type">{child.type}</span>
                <a href={child.url} target="_blank" rel="noreferrer" className="pv-child-summary">
                  {child.summary}
                </a>
                <StatusPill status={child.status} />
                <span className="pv-muted pv-child-assignee">{child.assignee || "Unassigned"}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </td>
    </tr>
  );
}

export default function EpicRow({ epic, steps, savingStep, onStepChange, onEpicChange }) {
  const [open, setOpen] = useState(false);
  const { total, done } = epic.children;

  return (
    <>
      <tr className={open ? "pv-row-open" : ""}>
        <td className="pv-project">
          <ProjectCell epic={epic} onSaved={onEpicChange} />
        </td>
        <td>
          <StepBar steps={steps} step={epic.step} saving={savingStep} onChange={onStepChange} />
        </td>
        <td className="pv-jira">
          <div className="pv-jira-row">
            <a href={epic.url} target="_blank" rel="noreferrer" className="pv-key">
              {epic.key}
            </a>
            <StatusMenu epic={epic} onChanged={(status) => onEpicChange({ status })} />
          </div>
          <div className="pv-jira-row pv-muted">{epic.assignee || "Unassigned"}</div>
          <div className="pv-jira-row pv-muted">
            {total ? (
              <button
                type="button"
                className="pv-link-btn pv-tickets-btn"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
              >
                {open ? "▾" : "▸"} {done} of {total} tickets done
              </button>
            ) : (
              "No tickets yet"
            )}
            {epic.updated ? ` · updated ${formatRelative(epic.updated)}` : ""}
          </div>
          {total ? (
            <div className="pv-progress">
              <span style={{ width: `${(100 * done) / total}%` }} />
            </div>
          ) : null}
        </td>
      </tr>
      {open ? <ChildrenRow epic={epic} /> : null}
    </>
  );
}
